'use client';

import React, { useState, useEffect } from 'react';
import { MockWizard } from './MockWizard';
import { MockLoading } from './MockLoading';
import { MockPreview } from './MockPreview';
import { MockDeploy } from './MockDeploy';
import { MockAnalytics } from './MockAnalytics';
import { MockSuccess } from './MockSuccess';
import { MockPayment } from './MockPayment';
import { Caption, CaptionProps } from './Caption';

export type DemoStep = 'wizard' | 'loading' | 'preview' | 'deploy' | 'payment' | 'analytics' | 'success' | 'final';

export function ProductDemoAnimation() {
    const [step, setStep] = useState<DemoStep>('wizard');
    const [caption, setCaption] = useState<CaptionProps | null>(null);

    // Orchestrate the flow
    // Orchestrate the flow
    const handleWizardComplete = () => {
        // Interstitial 1: AI Magic
        setCaption({
            type: 'interstitial',
            text: 'Thinking...',
            subtext: 'Analyzing your idea.'
        });

        setTimeout(() => {
            setStep('loading');

            setTimeout(() => {
                // Interstitial 2: The result
                setCaption({
                    type: 'interstitial',
                    text: 'Generated.',
                    subtext: 'Your tailored landing page.'
                });

                setTimeout(() => {
                    setStep('preview');
                    setCaption({
                        type: 'overlay',
                        text: 'Visual Editor',
                        subtext: 'Click to edit anything.'
                    });
                }, 1800);
            }, 2200);
        }, 1500);
    };

    const handlePreviewComplete = () => {
        // Interstitial 3: Ready to go
        setCaption({
            type: 'interstitial',
            text: 'Ready to Launch?',
            subtext: 'Setup in seconds.'
        });

        setTimeout(() => {
            setStep('deploy');
            setCaption({
                type: 'overlay',
                text: 'Custom Domain',
                subtext: 'Connect like a pro.'
            });
        }, 1500);
    };

    const handleDeployComplete = () => {
        setCaption({
            type: 'overlay',
            text: 'Pay Once',
            subtext: 'Lifetime hosting included.'
        });
        setStep('payment');
    };

    const handlePaymentComplete = () => {
        // Interstitial 4: Success
        setCaption({
            type: 'interstitial',
            text: 'Live in 3.. 2.. 1.. 🚀',
            subtext: 'Your idea is now a reality.'
        });

        setTimeout(() => {
            setStep('analytics');
            setCaption({
                type: 'overlay',
                text: 'Real Growth',
                subtext: 'Watch leads roll in.'
            });

            // After analytics, go to Final Hero state
            setTimeout(() => {
                setCaption({
                    type: 'hero',
                    text: 'Your Idea Deserves to be Seen.',
                    subtext: 'Don\'t let it wait. Launch today.',
                    cta: {
                        text: 'Build Now',
                        href: '/signup'
                    }
                });
                setStep('final');

                // Hold Hero state then restart
                setTimeout(() => {
                    // Fade out caption first
                    setCaption(null);

                    // Allow fade out time then reset
                    setTimeout(() => {
                        setStep('wizard');
                    }, 1500);
                }, 7000); // Hold hero message for 5s
            }, 5000); // Show analytics for 5s
        }, 3000);
    };

    // Initial caption for wizard
    useEffect(() => {
        if (step === 'wizard') {
            setCaption({
                type: 'overlay',
                text: 'Define your Idea',
                subtext: 'Who is it for? What does it do?'
            });
        }
    }, [step]);

    return (
        <div className="w-full h-auto mx-auto my-0 relative rounded-xl overflow-hidden shadow-2xl border border-gray-200 bg-white aspect-[9/16] md:aspect-[4/3] lg:aspect-[3/2]">
            {/* Browser Chrome / Header */}
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 flex items-center gap-2">
                <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="flex-1 text-center">
                    <div className="bg-white border border-gray-200 rounded-md py-1 px-3 text-xs text-gray-500 inline-flex items-center gap-2 mx-auto min-w-[300px] justify-center text-center">
                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                        waitlistly.live/signup
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="relative w-full h-full bg-white overflow-hidden">

                {/* Caption Layer */}
                {caption && (
                    <div className={`transition-opacity duration-500 ${caption.type === 'interstitial' ? 'z-50' : 'z-40'}`}>
                        <Caption {...caption} />
                    </div>
                )}

                {/* We use absolute positioning and transitions to fade between steps */}
                <div className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${step === 'wizard' ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}>
                    <MockWizard isActive={step === 'wizard'} onComplete={handleWizardComplete} />
                </div>

                <div className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${step === 'loading' ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}>
                    <MockLoading isActive={step === 'loading'} />
                </div>

                <div className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${step === 'preview' ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}>
                    <MockPreview isActive={step === 'preview'} onComplete={handlePreviewComplete} />
                </div>

                <div className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${step === 'deploy' || step === 'payment' ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}>
                    <MockDeploy isActive={step === 'deploy' || step === 'payment'} onComplete={handleDeployComplete} />
                </div>

                <div className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${step === 'payment' ? 'opacity-100 z-20' : 'opacity-0 z-0 pointer-events-none'}`}>
                    <MockPayment isActive={step === 'payment'} onComplete={handlePaymentComplete} />
                </div>

                <div className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${step === 'analytics' ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}>
                    <MockAnalytics isActive={step === 'analytics'} />
                </div>

            </div>
        </div>
    );
}
